import React from "react";
import {
  Container,
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import "../styles/Notifications.css";
import API from "../api/axiosConfig";
import { useToast } from "../context/ToastContext";

export default function NotificationPage() {
  const showToast = useToast();

  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
  const hourOptions = Array.from({ length: 13 }, (_, i) => i);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [editName, setEditName] = useState(null); // this is the notif that is being edited
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedDay, setSelectedDay] = useState(1);
  const [scheduledNotifs, setScheduledNotifs] = useState([]);
  const [triggeredNotifs, setTriggeredNotifs] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedTime, setSelectedTime] = useState("00:00");
  const [selectedMinutes, setSelectedMinutes] = useState(0);
  const [selectedHours, setSelectedHours] = useState(0);
  const [selectedSeconds, setSelectedSeconds] = useState(1);

  const minuteOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i * 5,
    label: (i * 5).toString(),
  }));
  const secondOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i * 5,
    label: (i * 5).toString(),
  }));

  const gridTemplate = {
    display: "grid",
    gridTemplateColumns: "1fr 2fr 1.5fr 1fr 1fr 1fr",
    gap: 2,
    alignItems: "center",
    px: 3,
    py: 1.5,
  };

  const getOrdinal = (n) => {
    if (!n) return "";
    const s = ["th", "st", "nd", "rd"],
      v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const handleRunNow = async (taskName) => {
    setButtonLoading(true);
    try {
      await API.post("/api/notification/run", { taskName: taskName });
      // really don't need to worry about response or processing anything
      showToast(taskName + " was run successfully");
    } catch (error) {
      showToast("Error running notifcation!", "error");
    } finally {
      setButtonLoading(false);
    }
  };

  // fetches notifications from the backend to display to the user
  const fetchNotifications = async () => {
    try {
      const response = await API.post("/api/notification/getAll");
      setScheduledNotifs(response.data.scheduled);
      setTriggeredNotifs(response.data.triggered);
    } catch (err) {
      console.error("Error fetching notifications");
    }
  };

  const handleTimeChange = (time) => {
    if (!time) return;

    const [hourStr, minuteStr] = time.split(":");
    setSelectedMinutes(parseInt(minuteStr, 10));
    setSelectedHours(parseInt(hourStr, 10));
    setSelectedTime(time);
  };

  const handleUpdate = async (notif) => {
    setButtonLoading(true);
    try {
      const payload = {
        taskName: notif.taskName,
        frequency: selectedFrequency,
        day: selectedDay,
        hour: selectedHours,
        minute: selectedMinutes,
        second: selectedSeconds,
        enabled: isEnabled,
      };
      await API.post("/api/notification/update", payload);
      // on ok resposne we'll update the notifs state
      notif.frequency = selectedFrequency;
      notif.day = selectedDay;
      notif.hour = selectedHours;
      notif.minute = selectedMinutes;
      notif.second = selectedSeconds;
      notif.enabled = isEnabled;
      showToast("Notification " + notif.taskName + "was successfully updated!");
    } catch (err) {
      console.error("Error updating notification");
    } finally {
      setButtonLoading(false);
    }
  };

  const handleFrequencyChange = (freq) => {
    setSelectedFrequency(freq);
    // Reset when menu changles
    setSelectedHours(0);
    setSelectedMinutes(0);
    setSelectedTime("00:00");
    setSelectedSeconds(1);
    setSelectedDay(1);
  };

  const handleTriggeredEdit = (toEdit) => {
    setEditName(toEdit.taskName);
    setIsEnabled(toEdit.enabled);

    if (editName === toEdit.taskName) {
      // user closing edit box
      // check if enabled toggle changed
      if (isEnabled !== toEdit.enabled) {
        // update the task
        handleUpdate(toEdit);
      }
      setEditName(null);
    }
  };

  const handleScheduledEdit = (toEdit) => {
    setEditName(toEdit.taskName);
    if (editName === toEdit.taskName) {
      // if you reclick on the edit button while it's already in edit mode for that task
      // clear state
      setEditName(null); // take it off edit mode
      setSelectedFrequency(null);
      setSelectedTime(null);
      setSelectedDay(null);
    }
    setIsEnabled(toEdit.enabled);
    setSelectedFrequency(toEdit.frequency);
    // set selected time with default time
    setSelectedHours(toEdit.hour);
    setSelectedMinutes(toEdit.minute);
    const hh = String(toEdit.hour || "00").padStart(2, "0");
    const mm = String(toEdit.minute || "00").padStart(2, "0");
    setSelectedTime(`${hh}:${mm}`);
    setSelectedDay(toEdit.day);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 950,
          p: { xs: 2, sm: 4 },
          borderRadius: 3,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          align="center"
          sx={{ mb: 4 }}
        >
          Notification Management
        </Typography>

        <Stack spacing={4}>
          {/* Scheduled Notifications Section */}
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, px: 1 }}>
              Scheduled Notifications
            </Typography>

            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  ...gridTemplate,
                  bgcolor: "action.hover",
                  borderBottom: "2px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  TASK NAME
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  FREQUENCY
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  DAY
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  TIME
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="text.secondary"
                  textAlign="right"
                />
              </Box>

              {/* Scheduled Notifications Rows */}
              <Box sx={{ p: 1 }}>
                {scheduledNotifs.map((n) => {
                  const time = `${n.hour % 12 || 12}:${String(n.minute || "00").padStart(2, "0")} ${
                    n.hour >= 12 ? "PM" : "AM"
                  }`;
                  const day =
                    n.frequency === "MONTHLY"
                      ? `${getOrdinal(n.day)} of every month`
                      : "Runs every day";
                  const isEditing = editName === n.taskName;
                  const isDimmed = editName !== null && !isEditing;

                  return (
                    <Paper
                      elevation={isEditing ? 8 : 1}
                      key={n.taskName}
                      sx={{
                        py: 0.5,
                        px: 1,
                        mb: 1,
                        minHeight: isEditing ? "auto" : "40px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        opacity: isDimmed ? 0.4 : 1,
                        filter: isDimmed ? "grayscale(100%) blur(1px)" : "none",
                        pointerEvents: isDimmed ? "none" : "auto",
                        position: isEditing ? "relative" : "static",
                        zIndex: isEditing ? 10 : 1,
                        transform: isEditing
                          ? "scaleX(1.02) scaleY(1.01)"
                          : "scale(1)",
                        border: "2px solid",
                        borderColor: isEditing ? "primary.main" : "transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: isEditing ? 8 : 1,
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ ...gridTemplate }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleScheduledEdit(n)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            variant="h6"
                            fontWeight="medium"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {n.taskName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {n.description}
                          </Typography>
                        </Box>

                        {isEditing ? (
                          <TextField
                            select
                            size="small"
                            value={selectedFrequency}
                            onChange={(e) =>
                              handleFrequencyChange(e.target.value)
                            }
                            variant="standard"
                            sx={{ minWidth: 100 }}
                            SelectProps={{ native: true }}
                          >
                            <option value="DAILY">Daily</option>
                            <option value="MONTHLY">Monthly</option>
                            <option value="HOURLY">Hourly</option>
                            <option value="INTERVAL">Interval</option>
                          </TextField>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {n.frequency.charAt(0) +
                              n.frequency.substring(1).toLowerCase()}
                          </Typography>
                        )}

                        {isEditing ? (
                          <Box
                            sx={{
                              minWidth: 150,
                              height: 45,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {selectedFrequency === "INTERVAL" ? (
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Runs every
                              </Typography>
                            ) : selectedFrequency === "DAILY" ? (
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Runs every day
                              </Typography>
                            ) : selectedFrequency === "MONTHLY" ? (
                              <TextField
                                select
                                label="Day of Month"
                                size="small"
                                value={selectedDay}
                                onChange={(e) => setSelectedDay(e.target.value)}
                                sx={{ minWidth: 100 }}
                                SelectProps={{ native: true }}
                              >
                                {dayOptions.map((d) => (
                                  <option key={d} value={d}>
                                    {getOrdinal(d)}
                                  </option>
                                ))}
                              </TextField>
                            ) : (
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Runs every hour:
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {day}
                          </Typography>
                        )}

                        {isEditing ? (
                          selectedFrequency === "HOURLY" ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 64,
                              }}
                            >
                              <TextField
                                select
                                size="small"
                                value={selectedMinutes}
                                label="minute"
                                onChange={(e) =>
                                  setSelectedMinutes(
                                    parseInt(e.target.value, 10),
                                  )
                                }
                                SelectProps={{ native: true }}
                                sx={{
                                  minWidth: 140,
                                  "& .MuiInputBase-root": { height: 40 },
                                }}
                              >
                                {minuteOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {getOrdinal(opt.label)}
                                  </option>
                                ))}
                              </TextField>
                            </Box>
                          ) : selectedFrequency === "INTERVAL" ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                                py: 2,
                              }}
                            >
                              <TextField
                                select
                                size="small"
                                value={selectedHours}
                                label="hr"
                                onChange={(e) =>
                                  setSelectedHours(parseInt(e.target.value, 10))
                                }
                                SelectProps={{ native: true }}
                                sx={{
                                  minWidth: 80,
                                  "& .MuiInputBase-root": { height: 40 },
                                }}
                              >
                                {hourOptions.map((opt) => (
                                  <option key={`hour-${opt}`} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </TextField>
                              <TextField
                                select
                                size="small"
                                value={selectedMinutes}
                                label="min"
                                onChange={(e) =>
                                  setSelectedMinutes(
                                    parseInt(e.target.value, 10),
                                  )
                                }
                                SelectProps={{ native: true }}
                                sx={{
                                  minWidth: 80,
                                  "& .MuiInputBase-root": { height: 40 },
                                }}
                              >
                                {minuteOptions.map((opt) => (
                                  <option
                                    key={`min-${opt.value}`}
                                    value={opt.value}
                                  >
                                    {opt.label}
                                  </option>
                                ))}
                              </TextField>
                              <TextField
                                select
                                size="small"
                                value={selectedSeconds}
                                label="sec"
                                onChange={(e) =>
                                  setSelectedSeconds(
                                    parseInt(e.target.value, 10),
                                  )
                                }
                                SelectProps={{ native: true }}
                                sx={{
                                  minWidth: 80,
                                  "& .MuiInputBase-root": { height: 40 },
                                }}
                              >
                                {secondOptions.map((opt) => (
                                  <option
                                    key={`sec-${opt.value}`}
                                    value={opt.value}
                                  >
                                    {opt.label}
                                  </option>
                                ))}
                              </TextField>
                            </Box>
                          ) : (
                            <TextField
                              size="small"
                              type="time"
                              value={selectedTime}
                              onChange={(e) => handleTimeChange(e.target.value)}
                              inputProps={{ step: 300 }}
                              sx={{ minWidth: 140 }}
                            />
                          )
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {time}
                          </Typography>
                        )}

                        <Box sx={{ textAlign: "left" }}>
                          <Stack
                            direction="column"
                            spacing={0}
                            alignItems="flex-end"
                          >
                            {isEditing && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleUpdate(n)}
                                disabled={buttonLoading}
                                sx={{
                                  textTransform: "none",
                                  minWidth: "auto",
                                  py: 0.2,
                                  fontSize: "0.85rem",
                                }}
                              >
                                Update
                              </Button>
                            )}
                            <Button
                              variant="text"
                              size="small"
                              color="success"
                              disabled={buttonLoading}
                              onClick={() => handleRunNow(n.taskName)}
                              sx={{
                                textTransform: "none",
                                minWidth: "auto",
                                py: 0.2,
                                fontSize: "0.85rem",
                                fontWeight: "bold",
                              }}
                            >
                              Run Now
                            </Button>
                          </Stack>
                        </Box>
                      </Box>

                      {isEditing && (
                        <Box
                          sx={{
                            mt: 0,
                            pt: 1,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FormControlLabel
                            sx={{
                              m: 0,
                              width: 120,
                              justifyContent: "flex-start",
                            }}
                            control={
                              <Switch
                                checked={isEnabled}
                                onChange={(e) => setIsEnabled(e.target.checked)}
                                color="primary"
                              />
                            }
                            label={isEnabled ? "Active" : "Disabled"}
                            labelPlacement="start"
                          />
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          </Box>

          {/* Triggered Notifications Section */}
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, px: 1 }}>
              Triggered Notifications
            </Typography>

            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, overflow: "hidden", p: 1 }}
            >
              {triggeredNotifs.map((n) => {
                const isEditing = editName === n.taskName;
                const isDimmed = editName !== null && !isEditing;

                return (
                  <Paper
                    elevation={isEditing ? 8 : 1}
                    key={n.taskName}
                    sx={{
                      py: 0.5,
                      px: 2,
                      mb: 0.75,
                      minHeight: isEditing ? "auto" : "40px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      opacity: isDimmed ? 0.4 : 1,
                      filter: isDimmed ? "grayscale(100%) blur(1px)" : "none",
                      pointerEvents: isDimmed ? "none" : "auto",
                      position: "relative",
                      zIndex: isEditing ? 10 : 1,
                      transform: isEditing
                        ? "scaleX(1.02) scaleY(1.01)"
                        : "scale(1)",
                      border: "2px solid",
                      borderColor: isEditing ? "primary.main" : "transparent",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: isEditing ? 8 : 1,
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 16,
                        top: isEditing ? 12 : "50%",
                        transform: isEditing ? "none" : "translateY(-50%)",
                        gap: 2,
                        px: 3,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleTriggeredEdit(n)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        minHeight: 48,
                        flexGrow: 1,
                        py: isEditing ? 2 : 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        sx={{ lineHeight: 1.1 }}
                      >
                        {n.taskName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.1, mt: 0.2 }}
                      >
                        {n.description}
                      </Typography>
                    </Box>

                    {isEditing && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          pt: 1,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <FormControlLabel
                          sx={{
                            m: 0,
                            width: 120,
                            justifyContent: "flex-start",
                          }}
                          control={
                            <Switch
                              checked={isEnabled}
                              onChange={(e) => setIsEnabled(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={isEnabled ? "Active" : "Disabled"}
                          labelPlacement="start"
                        />
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Paper>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
